import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import {
  Clock,
  Plus,
  Trash2,
  Calendar,
  X,
  Coffee,
  Moon,
  Sunrise,
  Wine,
  Users,
  CalendarOff,
  Copy,
  ClipboardPaste,
  AlertTriangle,
  Timer,
} from "lucide-react";
import {
  Alert,
  ALERT_BANNER_SURFACE_CLASS,
  Button,
  Input,
  Modal,
  ModalFooter,
  Badge,
  ConfirmModal,
  Label,
  EmptyState,
} from "@/components/ui";
import { LoadingState, SectionCard } from "@/components/common";
import {
  scheduleService,
  getWeekdayLabel,
  createEmptyWeeklySchedule,
  formatTime,
} from "@/services/scheduleService";
import type {
  TimeSlot,
  WeeklySchedule,
  ScheduleOverride,
  ScheduleOverrideCreate,
  Weekday,
  MealType,
} from "@/types";
import {
  ALL_WEEKDAYS,
  ActivityCategory,
  MealType as MealTypeValues,
} from "@/types";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/datetime";
import {
  addOneHourSameDay,
  slotsIntervalsOverlap,
} from "@/lib/scheduleSlotOverlap";

const MEAL_TYPE_OPTIONS: {
  value: MealType;
  icon: typeof Coffee;
  activeClass: string;
  labelKey: "breakfast" | "lunch" | "dinner" | "aperitif";
}[] = [
  {
    value: MealTypeValues.BREAKFAST,
    icon: Sunrise,
    activeClass: "border-orange-400 bg-orange-50 text-orange-700",
    labelKey: "breakfast",
  },
  {
    value: MealTypeValues.LUNCH,
    icon: Coffee,
    activeClass: "border-warning-400 bg-warning-50 text-warning-700",
    labelKey: "lunch",
  },
  {
    value: MealTypeValues.DINNER,
    icon: Moon,
    activeClass: "border-indigo-400 bg-indigo-50 text-indigo-700",
    labelKey: "dinner",
  },
  {
    value: MealTypeValues.APERITIF,
    icon: Wine,
    activeClass: "border-rose-400 bg-rose-50 text-rose-700",
    labelKey: "aperitif",
  },
];

function formatSlotLabel(slot: TimeSlot): string {
  const start = formatTime(slot.start_time);
  if (slot.end_time == null) {
    return start;
  }
  return `${start}–${formatTime(slot.end_time)}`;
}

function warnIfDaySlotsOverlap(slots: TimeSlot[], message: string): void {
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      if (slotsIntervalsOverlap(slots[i], slots[j])) {
        showSlotOverlapToast(message);
        return;
      }
    }
  }
}

function showSlotOverlapToast(message: string): void {
  toast(message, {
    icon: <AlertTriangle className="text-warning-500 h-5 w-5 shrink-0" />,
    className: cn(ALERT_BANNER_SURFACE_CLASS.warning, "text-warning-950"),
  });
}

function MealTypeIcon({ mealType }: { mealType: MealType }) {
  const option = MEAL_TYPE_OPTIONS.find((item) => item.value === mealType);
  if (!option) return null;
  const Icon = option.icon;
  const colorClass =
    mealType === "breakfast"
      ? "text-orange-500"
      : mealType === "lunch"
        ? "text-warning-500"
        : mealType === "dinner"
          ? "text-indigo-500"
          : "text-rose-500";
  return <Icon className={cn("h-3.5 w-3.5", colorClass)} />;
}

interface ScheduleSectionProps {
  activityId: string;
  activityType: ActivityCategory;
}

// ========== Slot Chip Component ==========
function SlotChip({
  slot,
  onRemove,
  onEdit,
}: {
  slot: TimeSlot;
  onRemove: () => void;
  onEdit: () => void;
}) {
  return (
    <div
      className="group border-surface-200 bg-surface-0 hover:border-primary-300 hover:bg-primary-50/40 dark:bg-surface-50/60 flex h-7 cursor-pointer items-center gap-1.5 rounded-md border px-2 text-[13px] shadow-xs transition-colors"
      onClick={onEdit}
    >
      <Clock className="text-surface-400 h-3.5 w-3.5" />
      <span className="text-surface-800 font-medium tabular-nums">
        {formatSlotLabel(slot)}
      </span>
      {slot.max_capacity && (
        <span className="text-surface-500 flex items-center gap-0.5 text-xs tabular-nums">
          <Users className="h-3 w-3" />
          {slot.max_capacity}
        </span>
      )}
      {slot.meal_type && (
        <span className="text-xs">
          <MealTypeIcon mealType={slot.meal_type} />
        </span>
      )}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="text-surface-400 hover:text-error-600 -mr-0.5 ml-0.5 rounded opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ========== Add/Edit Slot Modal ==========
interface SlotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (slot: TimeSlot) => void;
  editingSlot?: TimeSlot | null;
  existingSlots: TimeSlot[];
  excludeIndex?: number | null;
  isRestaurant: boolean;
}

function SlotModal({
  isOpen,
  onClose,
  onSave,
  editingSlot,
  existingSlots,
  excludeIndex = null,
  isRestaurant,
}: SlotModalProps) {
  const { t } = useTranslation("activities");
  const { t: tc } = useTranslation("common");
  const [startTime, setStartTime] = useState(
    editingSlot?.start_time ? formatTime(editingSlot.start_time) : "09:00",
  );
  /** `null` means start-only; a string means a time window with that end. */
  const [endTime, setEndTime] = useState<string | null>(
    editingSlot?.end_time != null ? formatTime(editingSlot.end_time) : null,
  );
  const [maxCapacity, setMaxCapacity] = useState<string>(
    editingSlot?.max_capacity?.toString() || "",
  );
  const [mealType, setMealType] = useState<MealType | "">(
    editingSlot?.meal_type || "",
  );

  const isWindow = endTime !== null;
  const duplicateStart = existingSlots.some((slot, index) => {
    if (excludeIndex !== null && index === excludeIndex) {
      return false;
    }
    return formatTime(slot.start_time) === formatTime(startTime);
  });
  const overlapsExisting = existingSlots.some((slot, index) => {
    if (excludeIndex !== null && index === excludeIndex) {
      return false;
    }
    return slotsIntervalsOverlap(
      { start_time: startTime, end_time: endTime },
      slot,
    );
  });

  const selectKind = (asWindow: boolean) => {
    if (asWindow) {
      setEndTime((current) => current ?? addOneHourSameDay(startTime));
      return;
    }
    setEndTime(null);
  };

  const handleSave = () => {
    if (!startTime) {
      toast.error(t("schedule.slotModal.startRequired"));
      return;
    }
    if (endTime !== null) {
      if (!endTime) {
        toast.error(t("schedule.slotModal.endRequired"));
        return;
      }
      if (formatTime(endTime) <= formatTime(startTime)) {
        toast.error(t("schedule.slotModal.endAfterStart"));
        return;
      }
    }

    if (duplicateStart) {
      return;
    }

    const slot: TimeSlot = {
      start_time: startTime,
      end_time: endTime,
      max_capacity: maxCapacity ? parseInt(maxCapacity, 10) : null,
      meal_type: mealType || null,
    };
    onSave(slot);
    onClose();
  };

  return (
    <Modal
      closeLabel={tc("close")}
      isOpen={isOpen}
      onClose={onClose}
      title={
        editingSlot
          ? t("schedule.slotModal.editTitle")
          : t("schedule.slotModal.addTitle")
      }
      size="md"
      footer={
        <ModalFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {tc("cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={duplicateStart}
          >
            {editingSlot
              ? t("schedule.slotModal.saveSlot")
              : t("schedule.slotModal.addSlot")}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => selectKind(false)}
            className={cn(
              "flex items-start gap-3 rounded-lg border p-3 text-left transition-[border-color,background-color,box-shadow]",
              !isWindow
                ? "border-primary-500 bg-primary-50/60 ring-primary-500/15 ring-[3px]"
                : "border-surface-200 hover:border-surface-300 hover:bg-surface-50",
            )}
          >
            <Clock
              className={cn(
                "mt-0.5 h-5 w-5 shrink-0",
                !isWindow ? "text-primary-600" : "text-surface-400",
              )}
            />
            <span>
              <span className="text-surface-900 block text-sm font-semibold">
                {t("schedule.slotModal.startOnlyTitle")}
              </span>
              <span className="text-surface-500 mt-0.5 block text-xs">
                {t("schedule.slotModal.startOnlySubtitle")}
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => selectKind(true)}
            className={cn(
              "flex items-start gap-3 rounded-lg border p-3 text-left transition-[border-color,background-color,box-shadow]",
              isWindow
                ? "border-primary-500 bg-primary-50/60 ring-primary-500/15 ring-[3px]"
                : "border-surface-200 hover:border-surface-300 hover:bg-surface-50",
            )}
          >
            <Timer
              className={cn(
                "mt-0.5 h-5 w-5 shrink-0",
                isWindow ? "text-primary-600" : "text-surface-400",
              )}
            />
            <span>
              <span className="text-surface-900 block text-sm font-semibold">
                {t("schedule.slotModal.windowTitle")}
              </span>
              <span className="text-surface-500 mt-0.5 block text-xs">
                {t("schedule.slotModal.windowSubtitle")}
              </span>
            </span>
          </button>
        </div>

        <div className={cn("grid gap-4", isWindow && "grid-cols-2")}>
          <Input
            type="time"
            label={t("schedule.slotModal.startTimeLabel")}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            error={
              duplicateStart
                ? t("schedule.slotModal.duplicateStartError")
                : undefined
            }
          />
          {endTime !== null && (
            <Input
              type="time"
              label={t("schedule.slotModal.endTimeLabel")}
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          )}
        </div>

        {overlapsExisting && !duplicateStart ? (
          <Alert variant="warning">
            {t("schedule.slotModal.overlapWarning")}
          </Alert>
        ) : null}

        <Alert variant="info">
          {t(
            isWindow
              ? "schedule.slotModal.windowHint"
              : "schedule.slotModal.startOnlyHint",
          )}
        </Alert>

        <div className="border-surface-200 bg-surface-50 rounded-lg border p-3">
          <Input
            type="number"
            label={t("schedule.slotModal.maxCapacityLabel")}
            placeholder={t("schedule.slotModal.maxCapacityPlaceholder")}
            value={maxCapacity}
            onChange={(e) => setMaxCapacity(e.target.value)}
            min={1}
            hint={t("schedule.slotModal.maxCapacityHint")}
            leftIcon={<Users className="h-4 w-4" />}
          />
        </div>

        {isRestaurant && (
          <div>
            <Label
              as="p"
              spacing="field"
            >
              {t("schedule.slotModal.mealTypeLabel")}
            </Label>
            <div className="flex flex-wrap gap-2">
              {MEAL_TYPE_OPTIONS.map(
                ({ value, icon: Icon, activeClass, labelKey }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setMealType(mealType === value ? "" : value)}
                    className={cn(
                      "flex h-8 items-center gap-2 rounded-lg border px-3 text-[13px] font-medium transition-colors",
                      mealType === value
                        ? activeClass
                        : "border-surface-200 text-surface-600 hover:border-surface-300",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {t(`schedule.slotModal.${labelKey}`)}
                  </button>
                ),
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ========== Override Card Component ==========
function OverrideCard({
  override,
  onEdit,
  onDelete,
}: {
  override: ScheduleOverride;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation("activities");
  return (
    <div className="border-surface-200 bg-surface-0 rounded-lg border px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-surface-900 text-sm font-medium">
            {override.name}
          </h4>
          <p className="text-surface-500 mt-0.5 text-[13px] tabular-nums">
            {formatDate(override.date_from)} — {formatDate(override.date_to)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {override.is_closed && (
            <Badge
              variant="error"
              size="sm"
            >
              {t("schedule.closedBadge")}
            </Badge>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onEdit}
          >
            <Calendar className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onDelete}
          >
            <Trash2 className="text-error-500 h-4 w-4" />
          </Button>
        </div>
      </div>
      {!override.is_closed && override.slots.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {override.slots.map((slot, idx) => (
            <span
              key={idx}
              className="bg-surface-100 text-surface-600 rounded-md px-1.5 py-0.5 text-xs tabular-nums"
            >
              {formatSlotLabel(slot)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ========== Override Modal ==========
interface OverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ScheduleOverrideCreate) => void;
  editingOverride?: ScheduleOverride | null;
  isRestaurant: boolean;
}

function OverrideModal({
  isOpen,
  onClose,
  onSave,
  editingOverride,
  isRestaurant,
}: OverrideModalProps) {
  const { t } = useTranslation("activities");
  const { t: tc } = useTranslation("common");
  const [name, setName] = useState(editingOverride?.name || "");
  const [dateFrom, setDateFrom] = useState(editingOverride?.date_from || "");
  const [dateTo, setDateTo] = useState(editingOverride?.date_to || "");
  const [isClosed, setIsClosed] = useState(editingOverride?.is_closed || false);
  const [slots, setSlots] = useState<TimeSlot[]>(editingOverride?.slots || []);
  const [slotModalOpen, setSlotModalOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<{
    slot: TimeSlot;
    index: number;
  } | null>(null);

  const handleSave = () => {
    if (!name || !dateFrom || !dateTo) {
      toast.error(t("schedule.overrideModal.nameDateRequired"));
      return;
    }
    if (dateFrom > dateTo) {
      toast.error(t("schedule.overrideModal.endAfterStart"));
      return;
    }

    onSave({
      name,
      date_from: dateFrom,
      date_to: dateTo,
      is_closed: isClosed,
      slots: isClosed ? [] : slots,
    });
    onClose();
  };

  const handleAddSlot = (slot: TimeSlot) => {
    if (editingSlot !== null) {
      const newSlots = [...slots];
      newSlots[editingSlot.index] = slot;
      setSlots(newSlots);
      setEditingSlot(null);
    } else {
      setSlots([...slots, slot]);
    }
  };

  const handleRemoveSlot = (index: number) => {
    setSlots(slots.filter((_, i) => i !== index));
  };

  return (
    <Modal
      closeLabel={tc("close")}
      isOpen={isOpen}
      onClose={onClose}
      title={
        editingOverride
          ? t("schedule.overrideModal.titleEdit")
          : t("schedule.overrideModal.titleAdd")
      }
      size="md"
      footer={
        <ModalFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {tc("cancel")}
          </Button>
          <Button onClick={handleSave}>
            {editingOverride
              ? t("schedule.overrideModal.saveChanges")
              : t("schedule.overrideModal.addOverrideBtn")}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        <Input
          label={t("schedule.overrideModal.nameLabel")}
          placeholder={t("schedule.overrideModal.namePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            type="date"
            label={t("schedule.overrideModal.from")}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <Input
            type="date"
            label={t("schedule.overrideModal.to")}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        <div>
          <Label
            as="p"
            spacing="field"
          >
            {t("schedule.overrideModal.status")}
          </Label>
          <button
            type="button"
            onClick={() => setIsClosed(!isClosed)}
            className={cn(
              "flex h-8 items-center gap-2 rounded-lg border px-3 text-[13px] font-medium transition-colors",
              isClosed
                ? "border-error-400 bg-error-50 text-error-700"
                : "border-surface-200 text-surface-600 hover:border-surface-300",
            )}
          >
            <CalendarOff className="h-4 w-4" />
            {isClosed
              ? t("schedule.overrideModal.closedDuring")
              : t("schedule.overrideModal.markClosed")}
          </button>
        </div>

        {!isClosed && (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label
                as="p"
                spacing="none"
              >
                {t("schedule.overrideModal.timeSlots")}
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                leftIcon={<Plus className="h-3 w-3" />}
                onClick={() => setSlotModalOpen(true)}
              >
                {t("schedule.overrideModal.addSlot")}
              </Button>
            </div>
            {slots.length === 0 ? (
              <EmptyState
                size="sm"
                showIcon={false}
                title={t("schedule.overrideModal.noSlotsHint")}
                className="items-start px-0 py-0 text-left"
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                {slots.map((slot, idx) => (
                  <SlotChip
                    key={idx}
                    slot={slot}
                    onEdit={() => {
                      setEditingSlot({ slot, index: idx });
                      setSlotModalOpen(true);
                    }}
                    onRemove={() => handleRemoveSlot(idx)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <SlotModal
        key={
          editingSlot
            ? `ov-${editingSlot.index}-${editingSlot.slot.start_time}-${editingSlot.slot.end_time ?? ""}-${editingSlot.slot.max_capacity ?? ""}-${editingSlot.slot.meal_type ?? ""}`
            : "ov-new"
        }
        isOpen={slotModalOpen}
        onClose={() => {
          setSlotModalOpen(false);
          setEditingSlot(null);
        }}
        onSave={handleAddSlot}
        editingSlot={editingSlot?.slot}
        existingSlots={slots}
        excludeIndex={editingSlot?.index ?? null}
        isRestaurant={isRestaurant}
      />
    </Modal>
  );
}

// ========== Main ScheduleSection Component ==========
export function ScheduleSection({
  activityId,
  activityType,
}: ScheduleSectionProps) {
  const { t } = useTranslation("activities");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const isRestaurant = activityType === ActivityCategory.RESTAURANT;

  const [slotModalOpen, setSlotModalOpen] = useState(false);
  const [editingDay, setEditingDay] = useState<Weekday | null>(null);
  const [editingSlotIndex, setEditingSlotIndex] = useState<number | null>(null);
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [editingOverride, setEditingOverride] =
    useState<ScheduleOverride | null>(null);
  const [pendingDeleteOverrideId, setPendingDeleteOverrideId] = useState<
    string | null
  >(null);
  const [copiedSlots, setCopiedSlots] = useState<TimeSlot[] | null>(null);
  const [copiedFromDay, setCopiedFromDay] = useState<Weekday | null>(null);

  // Fetch schedule
  const { data: schedule, isLoading } = useQuery({
    queryKey: ["schedule", activityId],
    queryFn: () => scheduleService.getSchedule(activityId),
    enabled: !!activityId,
  });

  // Mutations
  const updateWeeklyMutation = useMutation({
    mutationFn: (data: Partial<WeeklySchedule>) =>
      scheduleService.updateWeeklySchedule(activityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule", activityId] });
      toast.success(t("schedule.toast.updated"));
    },
    onError: (err) => {
      toastApiError(err, "generic", t("schedule.toast.updateError"));
    },
  });

  const createOverrideMutation = useMutation({
    mutationFn: (data: ScheduleOverrideCreate) =>
      scheduleService.createOverride(activityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule", activityId] });
      toast.success(t("schedule.toast.overrideCreated"));
    },
    onError: (err) => {
      toastApiError(err, "generic", t("schedule.toast.overrideCreateError"));
    },
  });

  const updateOverrideMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<ScheduleOverrideCreate>;
    }) => scheduleService.updateOverride(activityId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule", activityId] });
      toast.success(t("schedule.toast.overrideUpdated"));
    },
    onError: (err) => {
      toastApiError(err, "generic", t("schedule.toast.overrideUpdateError"));
    },
  });

  const deleteOverrideMutation = useMutation({
    mutationFn: (id: string) => scheduleService.deleteOverride(activityId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule", activityId] });
      setPendingDeleteOverrideId(null);
      toast.success(t("schedule.toast.overrideDeleted"));
    },
    onError: (err) => {
      toastApiError(err, "generic", t("schedule.toast.overrideDeleteError"));
    },
  });

  // Get current weekly schedule or empty
  const weeklySchedule = useMemo(
    () =>
      schedule
        ? {
            monday: schedule.monday || [],
            tuesday: schedule.tuesday || [],
            wednesday: schedule.wednesday || [],
            thursday: schedule.thursday || [],
            friday: schedule.friday || [],
            saturday: schedule.saturday || [],
            sunday: schedule.sunday || [],
          }
        : createEmptyWeeklySchedule(),
    [schedule],
  );

  const handleAddSlot = useCallback((day: Weekday) => {
    setEditingDay(day);
    setEditingSlotIndex(null);
    setSlotModalOpen(true);
  }, []);

  const handleEditSlot = useCallback((day: Weekday, index: number) => {
    setEditingDay(day);
    setEditingSlotIndex(index);
    setSlotModalOpen(true);
  }, []);

  const handleRemoveSlot = useCallback(
    (day: Weekday, index: number) => {
      const currentSlots = weeklySchedule[day];
      const newSlots = currentSlots.filter((_, i) => i !== index);
      updateWeeklyMutation.mutate({ [day]: newSlots });
    },
    [weeklySchedule, updateWeeklyMutation],
  );

  const handleCopyDay = useCallback(
    (day: Weekday) => {
      setCopiedSlots([...weeklySchedule[day]]);
      setCopiedFromDay(day);
      toast.success(
        t("schedule.toast.copiedDay", { day: getWeekdayLabel(day) }),
      );
    },
    [weeklySchedule, t],
  );

  const handlePasteDay = useCallback(
    (day: Weekday) => {
      if (copiedSlots) {
        warnIfDaySlotsOverlap(
          copiedSlots,
          t("schedule.toast.slotOverlapWarning"),
        );
        updateWeeklyMutation.mutate({ [day]: copiedSlots });
      }
    },
    [copiedSlots, updateWeeklyMutation, t],
  );

  const handleSaveSlot = useCallback(
    (slot: TimeSlot) => {
      if (!editingDay) return;

      const currentSlots = [...weeklySchedule[editingDay]];
      if (editingSlotIndex !== null) {
        currentSlots[editingSlotIndex] = slot;
      } else {
        currentSlots.push(slot);
      }

      // Sort by start time
      currentSlots.sort((a, b) => a.start_time.localeCompare(b.start_time));

      updateWeeklyMutation.mutate({ [editingDay]: currentSlots });
    },
    [editingDay, editingSlotIndex, weeklySchedule, updateWeeklyMutation],
  );

  const handleSaveOverride = useCallback(
    (data: ScheduleOverrideCreate) => {
      if (editingOverride) {
        updateOverrideMutation.mutate({ id: editingOverride.id, data });
      } else {
        createOverrideMutation.mutate(data);
      }
    },
    [editingOverride, createOverrideMutation, updateOverrideMutation],
  );

  const handleDeleteOverride = useCallback((id: string) => {
    setPendingDeleteOverrideId(id);
  }, []);

  if (isLoading) {
    return (
      <SectionCard
        title={t("schedule.title")}
        icon={Clock}
        className="mb-6"
      >
        <LoadingState
          message={tCommon("loading")}
          className="py-8"
        />
      </SectionCard>
    );
  }

  return (
    <>
      <SectionCard
        title={t("schedule.title")}
        icon={Clock}
        className="mb-6"
      >
        {/* Weekly Schedule */}
        <div className="mb-6 space-y-2.5">
          <h4 className="text-surface-900 text-sm font-semibold">
            {t("schedule.weeklyTitle")}
          </h4>
          <div className="divide-surface-200 border-surface-200 divide-y overflow-hidden rounded-lg border">
            {ALL_WEEKDAYS.map((day) => (
              <div
                key={day}
                className="bg-surface-0 hover:bg-surface-50 dark:hover:bg-surface-100/50 flex min-h-12 items-center gap-3 px-3 py-2 transition-colors"
              >
                <span className="text-surface-700 w-20 shrink-0 text-sm font-medium sm:w-24">
                  {getWeekdayLabel(day)}
                </span>
                <div className="flex flex-1 flex-wrap items-center gap-2">
                  {weeklySchedule[day].length === 0 ? (
                    <EmptyState
                      size="sm"
                      showIcon={false}
                      title={t("schedule.noSlotsClosed")}
                      className="items-start px-0 py-0 text-left"
                    />
                  ) : (
                    weeklySchedule[day].map((slot, idx) => (
                      <SlotChip
                        key={idx}
                        slot={slot}
                        onEdit={() => handleEditSlot(day, idx)}
                        onRemove={() => handleRemoveSlot(day, idx)}
                      />
                    ))
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {/* Copy button - only show if there are slots to copy */}
                  {weeklySchedule[day].length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => handleCopyDay(day)}
                      className={cn(
                        "text-surface-400 hover:text-surface-600",
                        copiedFromDay === day && "text-primary-500",
                      )}
                      title={t("schedule.copyScheduleTitle", {
                        day: getWeekdayLabel(day),
                      })}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  )}
                  {/* Paste button - only show if we have copied slots and it's a different day */}
                  {copiedSlots &&
                    copiedFromDay != null &&
                    copiedFromDay !== day && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => handlePasteDay(day)}
                        className="text-primary-500 hover:text-primary-700"
                        title={t("schedule.pasteScheduleTitle", {
                          day: getWeekdayLabel(copiedFromDay),
                        })}
                      >
                        <ClipboardPaste className="h-4 w-4" />
                      </Button>
                    )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => handleAddSlot(day)}
                    className="text-primary-500 hover:text-primary-700"
                    title={t("schedule.addTimeSlot")}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Schedule Overrides */}
        <div>
          <div className="mb-2.5 flex items-center justify-between gap-3">
            <h4 className="text-surface-900 text-sm font-semibold">
              {t("schedule.overridesTitle")}
            </h4>
            <Button
              type="button"
              variant="outline"
              size="sm"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setEditingOverride(null);
                setOverrideModalOpen(true);
              }}
            >
              {t("schedule.addOverride")}
            </Button>
          </div>

          {schedule?.overrides && schedule.overrides.length > 0 ? (
            <div className="space-y-2">
              {schedule.overrides.map((override) => (
                <OverrideCard
                  key={override.id}
                  override={override}
                  onEdit={() => {
                    setEditingOverride(override);
                    setOverrideModalOpen(true);
                  }}
                  onDelete={() => handleDeleteOverride(override.id)}
                />
              ))}
            </div>
          ) : (
            <p className="border-surface-200 text-surface-500 rounded-lg border border-dashed px-4 py-5 text-center text-[13px]">
              {t("schedule.noOverrides")}
            </p>
          )}
        </div>
      </SectionCard>

      {/* Slot Modal */}
      <SlotModal
        key={
          editingDay && editingSlotIndex !== null
            ? `wk-${editingDay}-${editingSlotIndex}`
            : editingDay
              ? `wk-add-${editingDay}`
              : "wk-slot"
        }
        isOpen={slotModalOpen}
        onClose={() => {
          setSlotModalOpen(false);
          setEditingDay(null);
          setEditingSlotIndex(null);
        }}
        onSave={handleSaveSlot}
        editingSlot={
          editingDay && editingSlotIndex !== null
            ? weeklySchedule[editingDay][editingSlotIndex]
            : null
        }
        existingSlots={editingDay ? weeklySchedule[editingDay] : []}
        excludeIndex={editingSlotIndex}
        isRestaurant={isRestaurant}
      />

      {/* Override Modal */}
      <OverrideModal
        key={editingOverride?.id ?? "new"}
        isOpen={overrideModalOpen}
        onClose={() => {
          setOverrideModalOpen(false);
          setEditingOverride(null);
        }}
        onSave={handleSaveOverride}
        editingOverride={editingOverride}
        isRestaurant={isRestaurant}
      />

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        isOpen={pendingDeleteOverrideId != null}
        onClose={() => {
          if (deleteOverrideMutation.isPending) return;
          setPendingDeleteOverrideId(null);
        }}
        onConfirm={() => {
          if (pendingDeleteOverrideId) {
            deleteOverrideMutation.mutate(pendingDeleteOverrideId);
          }
        }}
        title={tCommon("delete")}
        message={t("schedule.deleteOverrideConfirm")}
        confirmText={tCommon("delete")}
        isDestructive
        isLoading={deleteOverrideMutation.isPending}
      />
    </>
  );
}
